import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Grid, TextField, Button, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Autocomplete } from '@mui/material';
import { adminAPI } from '../../services/api';

const DAYS = [
  { id: 1, name: 'Lundi' }, { id: 2, name: 'Mardi' }, { id: 3, name: 'Mercredi' },
  { id: 4, name: 'Jeudi' }, { id: 5, name: 'Vendredi' }, { id: 6, name: 'Samedi' }
];

const CoursePlanning = () => {
  const [fullExport, setFullExport] = useState([]);
  const [modules, setModules]       = useState([]);
  const [groups, setGroups]         = useState([]);
  const [locations, setLocations]   = useState([]);
  const [schedules, setSchedules]   = useState([]);
  const [professors, setProfessors] = useState([]);
  const [saving, setSaving]         = useState(false);
  const exportLoaded = useRef(false); // load full-export only once

  const [formData, setFormData] = useState({
    module: null, group: null, day_of_week: 1,
    start_time: '08:00', end_time: '10:00',
    location: null, professor_name: ''
  });

  // Load full-export ONCE — modules + groups source (heavy, cached on server)
  const loadExportData = async () => {
    if (exportLoaded.current) return;
    try {
      const rows = await adminAPI.getDetailedStats(); // GET /admin/groups/stats/full-export
      const data = Array.isArray(rows) ? rows : [];
      setFullExport(data);
      exportLoaded.current = true;

      const moduleMap = new Map();
      data.forEach(row => {
        if (row.cod_elp && !moduleMap.has(row.cod_elp)) {
          moduleMap.set(row.cod_elp, {
            cod_elp: row.cod_elp,
            lib_elp: row.lib_elp || row.cod_elp,
            module_pattern: row.module_pattern,
          });
        }
      });
      setModules(
        Array.from(moduleMap.values()).sort((a, b) => (a.lib_elp || '').localeCompare(b.lib_elp || ''))
      );
    } catch (err) {
      console.error('Failed to load modules/groups:', err);
    }
  };

  // Load schedules + locations — called on mount and after each save
  const loadSchedules = async () => {
    try {
      const [schedsResponse, locs] = await Promise.all([
        adminAPI.getSchedules(),
        adminAPI.getLocations(),
      ]);
      setLocations(locs);
      // getSchedules() returns raw axios response — extract .data which is the array
      const schedsData = schedsResponse?.data ?? schedsResponse;
      setSchedules(Array.isArray(schedsData) ? schedsData : []);
    } catch (err) {
      console.error('Failed to load schedules:', err);
    }
  };

  const loadProfessors = async () => {
    try {
      const data = await adminAPI.getProfessors();
      setProfessors(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load professors:', err);
    }
  };

  useEffect(() => {
    loadExportData();
    loadSchedules();
    loadProfessors();
  }, []);

  const handleModuleChange = (module) => {
    setFormData(prev => ({ ...prev, module, group: null }));
    if (module) {
      const moduleGroups = fullExport
        .filter(row => row.cod_elp === module.cod_elp && row.group_name)
        .map(row => ({
          group_name: row.group_name,
          student_count: parseInt(row.student_count || 0),
        }))
        .sort((a, b) => a.group_name.localeCompare(b.group_name));
      // Deduplicate by group_name (same group can appear for multiple cod_elp rows)
      const seen = new Set();
      setGroups(moduleGroups.filter(g => seen.has(g.group_name) ? false : seen.add(g.group_name)));
    } else {
      setGroups([]);
    }
  };

  const handleSubmit = async () => {
    if (!formData.module || !formData.location || saving) return;
    setSaving(true);
    try {
      await adminAPI.createSchedule({
        module_code:    formData.module.cod_elp,
        module_name:    formData.module.lib_elp,
        group_name:     formData.group?.group_name || 'Tous',
        day_of_week:    formData.day_of_week,
        start_time:     formData.start_time,
        end_time:       formData.end_time,
        location:       formData.location.name,
        professor_name: formData.professor_name,
      });
      // Only refresh schedules list — not the heavy full-export
      await loadSchedules();
      setFormData({ module: null, group: null, day_of_week: 1, start_time: '08:00', end_time: '10:00', location: null, professor_name: '' });
      setGroups([]);
    } catch (err) {
      console.error('Failed to save schedule:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Programmation des Cours</Typography>
      <Grid container spacing={3}>

        {/* Form Side */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Nouvelle Séance</Typography>
            <Box display="flex" flexDirection="column" gap={2} mt={2}>

              <Autocomplete
                options={modules}
                getOptionLabel={(opt) => `${opt.lib_elp} (${opt.cod_elp})`}
                value={formData.module}
                onChange={(e, v) => handleModuleChange(v)}
                renderInput={(params) => <TextField {...params} label="Module" size="small" />}
              />

              <Autocomplete
                options={groups}
                getOptionLabel={(opt) => `${opt.group_name} — ${opt.student_count} étudiant(s)`}
                isOptionEqualToValue={(opt, val) => opt.group_name === val.group_name}
                value={formData.group}
                onChange={(e, v) => setFormData(prev => ({ ...prev, group: v }))}
                disabled={!formData.module}
                noOptionsText={formData.module ? 'Aucun groupe trouvé' : "Sélectionnez un module d'abord"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Groupe"
                    size="small"
                    helperText={
                      formData.group
                        ? `${formData.group.student_count} étudiants dans ce groupe`
                        : 'Optionnel — laisser vide pour Tous les groupes'
                    }
                  />
                )}
              />

              <TextField
                select label="Jour" value={formData.day_of_week}
                onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })} size="small"
              >
                {DAYS.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>

              <Box display="flex" gap={1}>
                <TextField type="time" label="Début" value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  fullWidth size="small" InputLabelProps={{ shrink: true }} />
                <TextField type="time" label="Fin" value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  fullWidth size="small" InputLabelProps={{ shrink: true }} />
              </Box>

              <Autocomplete
                options={locations}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={formData.location}
                onChange={(e, v) => setFormData({ ...formData, location: v })}
                renderInput={(params) => <TextField {...params} label="Local" size="small" />}
              />

              <Autocomplete
                freeSolo
                options={professors.map(p => p.name).sort((a, b) => a.localeCompare(b))}
                value={formData.professor_name}
                onInputChange={(e, val) => setFormData({ ...formData, professor_name: val })}
                renderInput={(params) => <TextField {...params} label="Professeur" size="small" />}
              />

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.module || !formData.location || saving}
              >
                {saving ? 'Enregistrement...' : 'Enregistrer la séance'}
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Schedules Table */}
        <Grid item xs={12} md={8}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Jour</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Groupe</TableCell>
                  <TableCell>Horaire</TableCell>
                  <TableCell>Lieu</TableCell>
                  <TableCell>Professeur</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Aucune séance programmée.</TableCell>
                  </TableRow>
                ) : (
                  schedules.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{DAYS.find(d => d.id === s.day_of_week)?.name}</TableCell>
                      <TableCell>{s.module_name}</TableCell>
                      <TableCell>{s.group_name || 'Tous'}</TableCell>
                      <TableCell>{s.start_time.slice(0, 5)} - {s.end_time.slice(0, 5)}</TableCell>
                      <TableCell>{s.location}</TableCell>
                      <TableCell>{s.professor_name || '—'}</TableCell>
                      <TableCell>
                        <Button color="error" size="small"
                          onClick={async () => {
                            await adminAPI.deleteSchedule(s.id);
                            loadSchedules();
                          }}>
                          Supprimer
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

      </Grid>
    </Box>
  );
};

export default CoursePlanning;